import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, enumLabel } from '@/lib/utils';

const badgeVariants = cva('inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap', {
  variants: {
    variant: {
      default: 'border-transparent bg-primary text-primary-foreground',
      secondary: 'border-transparent bg-secondary text-secondary-foreground',
      success: 'border-transparent bg-success/15 text-success',
      warning: 'border-transparent bg-warning/20 text-amber-700 dark:text-warning',
      destructive: 'border-transparent bg-destructive/15 text-destructive',
      info: 'border-transparent bg-blue-500/15 text-blue-600 dark:text-blue-400',
      outline: 'text-foreground',
    },
  },
  defaultVariants: { variant: 'default' },
});

export function Badge({ className, variant, ...props }: React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

const TONE: Record<string, VariantProps<typeof badgeVariants>['variant']> = {
  ACTIVE: 'success', READY: 'success', COMPLETED: 'success', ANSWERED: 'success', BRIDGED: 'success', OPEN: 'success', up: 'success',
  PAUSED: 'warning', WRAPUP: 'warning', PENDING: 'warning', DUE: 'warning', RETRY: 'warning', RESERVED: 'warning',
  RINGING: 'info', IN_CALL: 'info', IN_PROGRESS: 'info', DIALING: 'info', NEW: 'secondary', LOGGED_OUT: 'secondary',
  DISABLED: 'destructive', FAILED: 'destructive', DNC: 'destructive', STOPPED: 'destructive', CLOSED: 'destructive',
  DISCONNECTED: 'destructive', MISSED: 'destructive', CANCELLED: 'secondary', EXPIRED: 'secondary', ARCHIVED: 'secondary',
};

export function StatusBadge({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '') return null;
  const s = String(value);
  return <Badge variant={TONE[s] ?? 'outline'}>{enumLabel(s)}</Badge>;
}
